import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Settings,
  Email,
  WhatsApp,
  Notifications,
  ExpandMore,
  Save,
  Refresh,
  Warning,
} from '@mui/icons-material';
import { backofficeApi } from '../../services/backofficeApi';

interface NotificationSetting {
  id: string;
  notification_type: string;
  channel: string;
  is_enabled: boolean;
  template_id?: string;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface ChannelStatus {
  channel: string;
  is_globally_enabled: boolean;
  enabled_types: string[];
  disabled_types: string[];
  configuration: Record<string, any>;
}

const NOTIFICATION_TYPES = [
  'USER_REGISTRATION',
  'USER_SIGNUP_REQUEST',
  'USER_SIGNUP_APPROVED',
  'USER_SIGNUP_REJECTED',
  'USER_PASSWORD_CHANGED',
  'USER_PROMOTIONAL',
  'ORDER_PLACED',
  'ORDER_CONFIRMED',
  'ORDER_SHIPPED',
  'ORDER_DELIVERED',
  'ORDER_INVOICED',
  'ORDER_CLOSED',
  'ORDER_CANCELLED',
  'PAYMENT_RECEIVED',
  'PAYMENT_REJECTED',
  'PAYMENT_REMINDER',
  'BACKOFFICE_ORDER_PLACED',
];

const CHANNELS = ['EMAIL', 'WHATSAPP', 'IN_APP'];

const NotificationSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<NotificationSetting[]>([]);
  const [channelsStatus, setChannelsStatus] = useState<Record<string, ChannelStatus>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<NotificationSetting | null>(null);
  const [formData, setFormData] = useState({
    notification_type: '',
    channel: '',
    is_enabled: true,
    settings: '{}',
  });

  useEffect(() => {
    loadSettings();
    loadChannelsStatus();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await backofficeApi.get('/backoffice/notification-settings/');
      setSettings(response.data.settings);
    } catch (error: any) {
      setError('Failed to load settings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadChannelsStatus = async () => {
    try {
      const response = await backofficeApi.get('/backoffice/notification-settings/channels/status');
      setChannelsStatus(response.data.channels);
    } catch (error: any) {
      console.error('Failed to load channels status:', error);
    }
  };

  const handleToggleChannel = async (channel: string, enabled: boolean) => {
    try {
      setError('');
      setSuccess('');
      await backofficeApi.put(`/backoffice/notification-settings/channel/${channel}/toggle`, null, {
        params: { is_enabled: enabled }
      });
      setSuccess(`Channel ${channel} ${enabled ? 'enabled' : 'disabled'} successfully`);
      loadSettings();
      loadChannelsStatus();
    } catch (error: any) {
      setError('Failed to toggle channel: ' + error.message);
    }
  };

  const handleInitializeDefaults = async () => {
    try {
      setError('');
      setSuccess('');
      await backofficeApi.post('/backoffice/notification-settings/initialize');
      setSuccess('Default settings initialized successfully');
      loadSettings();
      loadChannelsStatus();
    } catch (error: any) {
      setError('Failed to initialize defaults: ' + error.message);
    }
  };

  const handleOpenDialog = (setting?: NotificationSetting) => {
    if (setting) {
      setEditingSetting(setting);
      setFormData({
        notification_type: setting.notification_type,
        channel: setting.channel,
        is_enabled: setting.is_enabled,
        settings: JSON.stringify(setting.settings, null, 2),
      });
    } else {
      setEditingSetting(null);
      setFormData({
        notification_type: '',
        channel: '',
        is_enabled: true,
        settings: '{}',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSetting(null);
  };

  const handleSave = async () => {
    try {
      const settingData = {
        ...formData,
        settings: JSON.parse(formData.settings),
      };

      await backofficeApi.post('/backoffice/notification-settings/', settingData);
      setSuccess('Setting saved successfully');
      handleCloseDialog();
      loadSettings();
      loadChannelsStatus();
    } catch (error: any) {
      setError('Failed to save setting: ' + error.message);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'email':
        return <Email color="primary" />;
      case 'whatsapp':
        return <WhatsApp color="success" />;
      case 'in_app':
        return <Notifications color="secondary" />;
      default:
        return <Notifications />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'email':
        return 'primary';
      case 'whatsapp':
        return 'success';
      case 'in_app':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getSettingsByChannel = (channel: string) => {
    return settings.filter(s => s.channel === channel);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Notification Settings
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Configure notification channels and types
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              loadSettings();
              loadChannelsStatus();
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Settings />}
            onClick={handleInitializeDefaults}
          >
            Initialize Defaults
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {/* Channel Overview Cards */}
          <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            {CHANNELS.map((channel) => {
              const status = channelsStatus[channel];
              return (
                <Box key={channel} sx={{ flex: '1 1 300px', minWidth: '280px' }}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getChannelIcon(channel)}
                          <Typography variant="h6">
                            {channel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={status?.is_globally_enabled || false}
                              onChange={(e) => handleToggleChannel(channel, e.target.checked)}
                              color={getChannelColor(channel) as any}
                            />
                          }
                          label=""
                        />
                      </Box>
                      
                      {status && (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                              Enabled Types: {status.enabled_types.length}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Disabled Types: {status.disabled_types.length}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Chip
                              label={status.is_globally_enabled ? 'Active' : 'Inactive'}
                              color={status.is_globally_enabled ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>

          {/* Detailed Settings by Channel */}
          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Channel Configuration Details
              </Typography>
              
              {CHANNELS.map((channel) => {
                const channelSettings = getSettingsByChannel(channel);
                const status = channelsStatus[channel];
                
                return (
                  <Accordion key={channel} sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
                        {getChannelIcon(channel)}
                        <Typography variant="subtitle1">
                          {channel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Settings
                        </Typography>
                        <Chip
                          label={`${channelSettings.length} configurations`}
                          size="small"
                          color="primary"
                        />
                        {!status?.is_globally_enabled && (
                          <Chip
                            label="Channel Disabled"
                            size="small"
                            color="error"
                            icon={<Warning />}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails>
                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">
                            Notification Type Settings
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, channel }));
                              handleOpenDialog();
                            }}
                          >
                            Add Setting
                          </Button>
                        </Box>
                        
                        {channelSettings.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Notification Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last Updated</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {channelSettings.map((setting) => (
                                <TableRow key={setting.id}>
                                  <TableCell>
                                    {setting.notification_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={setting.is_enabled ? 'Enabled' : 'Disabled'}
                                      color={setting.is_enabled ? 'success' : 'default'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {new Date(setting.updated_at).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      size="small"
                                      onClick={() => handleOpenDialog(setting)}
                                    >
                                      Edit
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                            No settings configured for this channel. Click "Add Setting" to create one.
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSetting ? 'Edit Notification Setting' : 'Create Notification Setting'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                value={formData.notification_type}
                onChange={(e) => setFormData({ ...formData, notification_type: e.target.value })}
                label="Notification Type"
              >
                {NOTIFICATION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                value={formData.channel}
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                label="Channel"
                disabled={!!editingSetting}
              >
                {CHANNELS.map((channel) => (
                  <MenuItem key={channel} value={channel}>
                    {channel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_enabled}
                  onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                />
              }
              label="Enabled"
            />

            <TextField
              fullWidth
              label="Channel Settings (JSON)"
              value={formData.settings}
              onChange={(e) => setFormData({ ...formData, settings: e.target.value })}
              multiline
              rows={4}
              placeholder='{"setting_name": "value"}'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<Save />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSettingsPage;